import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { ArrowBack, Chat as ChatIcon, GetApp } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDeckDetail } from '../hooks/useDeckDetail';
import { DeckOverview } from '../components/analysis/DeckOverview';
import { AnalysisProgress } from '../components/analysis/AnalysisProgress';
import { ExportDialog } from '../components/export/ExportDialog';
import { LoadingOverlay } from '../components/common/LoadingOverlay';
import { ErrorDisplay } from '../components/common/ErrorDisplay';

export const AnalysisDashboard: React.FC = () => {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const { deck, loading, error, refresh } = useDeckDetail(deckId!);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  if (loading) {
    return <LoadingOverlay open={true} message="Loading analysis..." />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <ErrorDisplay error={error} onRetry={refresh} />
      </Container>
    );
  }

  if (!deck) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography>Deck not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={3}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/decks')}
            sx={{ cursor: 'pointer' }}
          >
            My Presentations
          </Link>
          <Typography color="text.primary">{deck.fileName}</Typography>
        </Breadcrumbs>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/decks')}
            >
              Back
            </Button>
            <Typography variant="h4" fontWeight={600}>
              Analysis Dashboard
            </Typography>
          </Box>

          {deck.status === 'completed' && (
            <Box display="flex" alignItems="center" gap={1}>
              {/* Keep Chat button code but hide it */}
              <Button
                variant="outlined"
                startIcon={<ChatIcon />}
                onClick={() => navigate(`/chat/${deckId}`)}
                sx={{ display: 'none' }}
              >
                Open Chat
              </Button>

              {/* New Export Report button in place of Chat */}
              <Button
                variant="contained"
                startIcon={<GetApp />}
                onClick={() => setExportDialogOpen(true)}
              >
                Export Report
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <AnalysisProgress deck={deck} />

      {deck.status === 'completed' && (
        <DeckOverview deck={deck} />
      )}

      {deck.status === 'failed' && (
        <ErrorDisplay
          error="Analysis failed. Please try uploading again."
          title="Analysis Failed"
        />
      )}

      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        deckId={deckId!}
        deckName={deck.fileName}
      />
    </Container>
  );
};

